/**
 * Lead detail — a full page (build brief §7, decided full page over a panel).
 *
 * Its own route (`/leads/:id`), reached from the pipeline table. Top to bottom:
 * the stage and where it came from, the five-step stage strip, the next step,
 * the contact and who captured it, the came-from facts the tracker saw, and the
 * stage history newest first. A sticky footer carries the three decisions —
 * Not interested, No response, Move stage — so they are in reach without
 * scrolling back up. The content reuses the client-detail layout classes; only
 * the stage strip, came-from grid and footer are its own.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApp } from '../store/AppContext.jsx';
import api from '../lib/api.js';
import { isOffline } from '../lib/storage.js';
import { parseDate } from '../lib/dates.js';
import Icon from '../lib/icons.jsx';
import { digitsOnly } from '../lib/phone.js';
import { Avatar, OwnerPill, StagePill, Pill, Dash, EmptyState } from '../components/Primitives.jsx';
import {
  companyName, contactOf, leadDetails, clientOfDeal,
  DISPLAY_STAGES, displayStage, subStatus
} from '../domain/crm.js';

const Row = ({ label, children }) => (
  <div className="cdp-row">
    <span className="cdp-lab">{label}</span>
    <span className="cdp-val">{children ?? <Dash />}</span>
  </div>
);

const Panel = ({ icon, title, children, action }) => (
  <section className="cdp-panel">
    <h3><span className="cdp-ic"><Icon name={icon} /></span>{title}{action}</h3>
    <div className="cdp-panelbody">{children}</div>
  </section>
);

export default function LeadDetail() {
  const { id } = useParams();
  const { S, crm, rows, fmtDate, setSheet, people } = useApp();
  const d = crm.deals.find(x => String(x.id) === String(id));

  const [events, setEvents] = useState([]);

  /* The append-only stage history, newest first. Skipped offline, where there
     is no server; the sample workspace shows the arrival line alone. */
  useEffect(() => {
    if (!d || isOffline()) { setEvents([]); return; }
    let live = true;
    api.deals.events(d.id)
      .then(res => { if (live) setEvents(((res && res.data) || []).slice().reverse()); })
      .catch(() => { if (live) setEvents([]); });
    return () => { live = false; };
  }, [d?.id]);

  const f = useMemo(() => (d ? leadDetails(crm, d) : null), [d, crm]);

  if (!d) {
    return (
      <div className="cdp">
        <Link to="/pipeline" className="cdp-crumb"><Icon name="chev" />Pipeline</Link>
        <EmptyState icon="alert" tone="g-red" title="That lead is no longer in the pipeline">
          It may have been won, dropped, or filtered out.
        </EmptyState>
      </div>
    );
  }

  const company = companyName(crm, d.cid);
  const contact = contactOf(crm, d.contact);
  /* The other half of the pair: if this lead has become a client, the two
     records link both ways — the client page points back here, this points
     forward to the account. */
  const linkedClient = clientOfDeal(rows, d);
  const email = (contact && contact.email) || f.email || '';
  const phone = (contact && contact.phone) || f.phone || '';
  const location = f.place || [f.city, f.country].filter(Boolean).join(', ');
  const requested = parseDate(f.requested);
  const waNumber = digitsOnly(phone);

  /* The five display stages (brief §6). A Closed lead shows every step done;
     otherwise steps before the current one are done and the current one is live. */
  const steps = (S.stages && S.stages.length) ? S.stages : DISPLAY_STAGES;
  const cur = displayStage(d);
  const curIndex = steps.indexOf(cur);
  const isWon = cur === 'Closed';

  /* Who captured this lead, from the server-set capture fields (§9). The user
     id resolves to a name through the directory; an automatic capture has no
     person and names its source instead. */
  const capturedByName = d.capturedById
    ? ((people || []).find(p => String(p.id) === String(d.capturedById)) || {}).name || null
    : null;
  const captureMethod = ({
    automatic: `Captured automatically${d.origin ? ` · ${d.origin}` : ''}`,
    'single entry': 'Entered by hand',
    'bulk upload': 'Bulk upload'
  })[d.captureMethod] || (d.origin ? `Captured automatically · ${d.origin}` : '—');

  return (
    <div className="cdp">
      <Link to="/pipeline" className="cdp-crumb"><Icon name="chev" />Pipeline</Link>

      <header className="cdp-hero">
        <Avatar name={d.title} className="cdp-ava" />
        <div className="cdp-id">
          <div className="cdp-name">
            <h1>{d.title || 'Untitled lead'}</h1>
            <StagePill value={cur} />
            {subStatus(d) ? <Pill tone="gr-slate">{subStatus(d)}</Pill> : null}
          </div>
          <div className="cdp-sub">
            {company ? <span><Icon name="building" />{company}</span> : null}
            {location ? <span><Icon name="pin" />{location}</span> : null}
            <span className="cdp-code">{d.leadNo ? `L-${d.leadNo}` : `Lead ${String(d.id).slice(0, 8)}`}</span>
            {linkedClient ? (
              <Link to={`/clients/${linkedClient.id}`} className="cdp-link">
                <Icon name="check" />Now a client — {linkedClient.name}
              </Link>
            ) : null}
          </div>
        </div>
        <div className="cdp-acts">
          <a className="cdp-btn" href={phone ? `tel:${phone}` : undefined} aria-disabled={!phone}>
            <Icon name="phone" />Call
          </a>
          <a className="cdp-btn" href={waNumber ? `https://wa.me/${waNumber}` : undefined}
            target="_blank" rel="noreferrer" aria-disabled={!waNumber}>
            <Icon name="chat" />WhatsApp
          </a>
          <a className="cdp-btn" href={email ? `mailto:${email}` : undefined} aria-disabled={!email}>
            <Icon name="mail" />Email
          </a>
          <button type="button" className="cdp-btn" onClick={() => setSheet({ kind: 'deal', id: d.id })}>
            <Icon name="edit" />Edit
          </button>
        </div>
      </header>

      {/* Five-step stage strip: done steps brand, current in its stage colour,
          future grey (brief §7). */}
      <div className="cdp-life ld-strip">
        {steps.map((s, i) => {
          const done = isWon || (curIndex >= 0 && i < curIndex);
          const here = !isWon && i === curIndex;
          return (
            <div key={s} className={`cdp-step${done ? ' on' : ''}${here ? ' here' : ''}`}>
              <span className="cdp-dot">{done ? <Icon name="check" /> : i + 1}</span>
              <b>{s}</b>
            </div>
          );
        })}
      </div>

      <div className="cdp-cols">
        <div className="cdp-main">
          {d.next ? (
            <Panel icon="flow" title="Next step">
              <p className="ld-next">{d.next}</p>
            </Panel>
          ) : null}

          {/* Came from: what the tracker saw about how this lead arrived. */}
          <Panel icon="zap" title="Came from">
            <div className="cdp-panelbody">
              <Row label="Source">{d.source || null}</Row>
              <Row label="First seen">{requested ? fmtDate(requested, true) : null}</Row>
              <Row label="Campaign">{f.campaign || null}</Row>
              <Row label="Medium">{f.utm.medium || null}</Row>
              <Row label="Landing page">
                {f.landing ? <a href={f.landing} target="_blank" rel="noreferrer">{f.landingPath || f.landing}</a> : null}
              </Row>
              <Row label="Device">{[f.device, f.os, f.browser].filter(Boolean).join(' · ') || null}</Row>
            </div>
          </Panel>

          {/* Stage history, newest first, each with its comment and author. */}
          <Panel icon="clock" title="Stage history">
            {events.length ? (
              <ul className="cdp-hist">
                {events.map((e, i) => (
                  <li key={e.id || i} className={`cdp-hev ${e.kind}`}>
                    <span className="cdp-hdot" />
                    <div className="cdp-htext">
                      <b>
                        {e.from_value ? `${e.from_value} → ` : ''}{e.to_value || e.kind}
                      </b>
                      {e.note ? <p className="ld-note">{e.note}</p> : null}
                      <small>
                        {e.actor || 'system'}{e.at ? ` · ${fmtDate(e.at, true)}` : ''}
                      </small>
                    </div>
                  </li>
                ))}
                <li className="cdp-hev arrived">
                  <span className="cdp-hdot" />
                  <div className="cdp-htext">
                    <b>Arrived on the pipeline</b>
                    <small>{requested ? fmtDate(requested, true) : (d.source || 'added')}</small>
                  </div>
                </li>
              </ul>
            ) : (
              <div className="cdp-muted">
                {isOffline() ? 'Stage history is not available offline.' : 'No stage moves recorded yet.'}
              </div>
            )}
          </Panel>
        </div>

        <aside className="cdp-side">
          <Panel icon="users" title="Contact">
            <Row label="Contact">{(contact && contact.name) || d.title || null}</Row>
            <Row label="Email">{email ? <a href={`mailto:${email}`}>{email}</a> : null}</Row>
            <Row label="Phone">{phone ? <a href={`tel:${phone}`}>{phone}</a> : null}</Row>
            <Row label="Role">{(contact && contact.role) || null}</Row>
            <Row label="Owner"><OwnerPill name={d.owner} /></Row>
          </Panel>

          {/* Captured by (brief §7 / §9): who brought this lead in. */}
          <Panel icon="flag" title="Captured by">
            <Row label="By">{capturedByName || <Pill tone="gr-slate">Automatic</Pill>}</Row>
            <Row label="How">{captureMethod}</Row>
            <Row label="When">
              {d.capturedAt ? fmtDate(d.capturedAt, true) : requested ? fmtDate(requested, true) : null}
            </Row>
          </Panel>
        </aside>
      </div>

      {/* Sticky footer: the three decisions, always in reach (brief §7). */}
      <div className="ld-foot">
        {!d.dropped ? (
          <>
            <button type="button" className="cdp-btn danger" onClick={() => setSheet({ kind: 'notInterested', id: d.id })}>
              <Icon name="x" />Not interested
            </button>
            <button type="button" className="cdp-btn" onClick={() => setSheet({ kind: 'noResponse', id: d.id })}>
              <Icon name="eyeoff" />No response
            </button>
          </>
        ) : <span className="cdp-muted">This lead is off the board.</span>}
        <button type="button" className="cdp-btn primary" onClick={() => setSheet({ kind: 'moveStage', id: d.id })}>
          <Icon name="flow" />Move stage
        </button>
      </div>
    </div>
  );
}
